import { supabase } from '../lib/supabase';

/**
 * Authentication Service
 * Handles all authentication operations with Supabase
 * Converts between snake_case (database) and camelCase (React)
 */

export const authService = {
  /**
   * Sign up a new user with profile data
   * @param {Object} userData - User registration data
   * @returns {Promise<{data, error}>}
   */
  async signUp(userData) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email: userData?.email?.trim()?.toLowerCase(),
        password: userData?.password,
        options: {
          emailRedirectTo: `${window.location.origin}/check-email?verified=1`,
          data: {
            first_name: userData?.firstName,
            last_name: userData?.lastName,
            phone: userData?.phone || '',
            country: userData?.country,
            institution: userData?.institution,
            institution_type: userData?.institutionType || null,
            department: userData?.department || '',
            academic_rank: userData?.academicRank || null,
            orcid: userData?.orcid || '',
            website: userData?.website || '',
            subject_areas: userData?.subjectAreas || [],
            roles: userData?.roles || [],
            experience_level: userData?.experienceLevel || null,
            avatar_url: userData?.avatarUrl || '',
            receive_updates: userData?.receiveUpdates || false,
            available_as_reviewer: userData?.availableAsReviewer !== false,
            reviewer_keywords: userData?.reviewerKeywords || [],
            // Consent record (Law 18-07). Stored in auth metadata as proof.
            terms_accepted_at: userData?.termsAcceptedAt || null,
            terms_version: userData?.termsVersion || null
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign in an existing user
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{data, error}>}
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign out the current user
   * @returns {Promise<{error}>}
   */
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  },

  /**
   * Get the current user session
   * @returns {Promise<{data, error}>}
   */
  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get session error:', error);
      return { data: null, error };
    }
  },

  /**
   * Get the current user
   * @returns {Promise<{data, error}>}
   */
  async getUser() {
    try {
      const { data, error } = await supabase?.auth?.getUser();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get user error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user password
   * @param {string} newPassword
   * @returns {Promise<{data, error}>}
   */
  async updatePassword(newPassword) {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { data: null, error };
    }
  },

  /**
   * Send password reset email
   * @param {string} email
   * @returns {Promise<{data, error}>}
   */
  async resetPassword(email) {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { data: null, error };
    }
  }
};
